/**
 * @file Writes a decimal amount as compact currency: `$x.xxM` at a million or
 * more, `$xK` at a thousand or more and whole dollars below, each rounded half
 * away from zero. `millionPlaces` narrows the millions, as the chart bar total
 * does. Consumers: the "Sales credit by month" bar labels and the signed and
 * bar total texts. The threshold is tested on the exact amount, so 999,600
 * reads `"$1000K"`. It throws on a string that is not a decimal, so callers
 * verify cells first.
 */
import { analyticsDecimalFraction, analyticsFractionDecimal } from '#types';

const MILLION = 1_000_000n;
const THOUSAND = 1_000n;
const MILLION_PLACES = 2;

export const analyticsCompactCurrencyText = (
  decimal: string,
  options?: { millionPlaces?: number },
): string => {
  const fraction = analyticsDecimalFraction(decimal);
  if (fraction === undefined) throw new Error(`"${decimal}" is not a decimal.`);
  const { numerator, denominator } =
    fraction.numerator < 0n
      ? { numerator: -fraction.numerator, denominator: fraction.denominator }
      : fraction;
  const text =
    numerator >= MILLION * denominator
      ? `${analyticsFractionDecimal({ numerator, denominator: denominator * MILLION }, options?.millionPlaces ?? MILLION_PLACES)}M`
      : numerator >= THOUSAND * denominator
        ? `${analyticsFractionDecimal({ numerator, denominator: denominator * THOUSAND }, 0)}K`
        : analyticsFractionDecimal({ numerator, denominator }, 0);
  const sign = fraction.numerator < 0n && text !== '0' ? '-' : '';
  return `${sign}$${text}`;
};
